/**
 * Feature Showcase Component
 * Handles scroll-triggered animations and content updates for the PureOne feature showcase
 */

export default function featureShowcase() {
    return {
        currentFeature: 0,
        features: [],
        isObserving: true,
        observer: null,

        init() {
            // Query all feature items
            const featureItems = document.querySelectorAll('.feature-showcase__item');

            // Build features array from DOM elements
            this.features = Array.from(featureItems).map((item, index) => ({
                title: item.dataset.featureTitle || '',
                description: item.dataset.featureDescription || '',
                highlight: item.dataset.featureHighlight || '',
                element: item,
                index: index
            }));

            // Set up Intersection Observer for scroll-triggered updates
            const options = {
                root: null,
                rootMargin: '-50% 0px',
                threshold: 0
            };

            this.observer = new IntersectionObserver((entries) => {
                if (!this.isObserving) return;

                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const index = parseInt(entry.target.dataset.featureIndex || 0);
                        if (index !== this.currentFeature) {
                            this.currentFeature = index;
                            this.updateProgressIndicators();
                            this.updateFixedContent();
                        }
                    }
                });
            }, options);

            // Observe each feature item
            featureItems.forEach(item => {
                this.observer.observe(item);
            });

            // Initial update
            this.updateProgressIndicators();
            this.updateFixedContent();
        },

        updateProgressIndicators() {
            // Update progress dots
            const dots = document.querySelectorAll('[data-progress-dot]');
            dots.forEach((dot, index) => {
                if (index === this.currentFeature) {
                    dot.classList.add('active');
                    dot.classList.remove('inactive');
                } else {
                    dot.classList.remove('active');
                    dot.classList.add('inactive');
                }
            });
        },

        updateFixedContent() {
            // Update fixed title
            const fixedTitle = this.$refs.fixedTitle;
            const fixedDescription = this.$refs.fixedDescription;
            const fixedHighlight = this.$refs.fixedHighlight;

            if (this.features[this.currentFeature]) {
                const feature = this.features[this.currentFeature];

                // Animate content change
                if (fixedTitle) {
                    fixedTitle.style.opacity = '0';
                    setTimeout(() => {
                        fixedTitle.textContent = feature.title;
                        fixedTitle.style.opacity = '1';
                    }, 150);
                }

                if (fixedDescription) {
                    fixedDescription.style.opacity = '0';
                    setTimeout(() => {
                        fixedDescription.textContent = feature.description;
                        fixedDescription.style.opacity = '1';
                    }, 150);
                }

                if (fixedHighlight) {
                    fixedHighlight.style.opacity = '0';
                    setTimeout(() => {
                        fixedHighlight.textContent = feature.highlight;
                        fixedHighlight.style.opacity = '1';
                    }, 150);
                }
            }
        },

        goToFeature(index) {
            // Scroll to specific feature
            if (this.features[index] && this.features[index].element) {
                this.isObserving = false;

                this.features[index].element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });

                // Re-enable observation after scroll
                setTimeout(() => {
                    this.isObserving = true;
                    this.currentFeature = index;
                    this.updateProgressIndicators();
                    this.updateFixedContent();
                }, 1000);
            }
        },

        destroy() {
            // Clean up observer if component is destroyed
            if (this.observer) {
                this.observer.disconnect();
            }
        }
    };
}